import { forwardRef } from 'react'
import clsx from 'clsx'
import pointbarPolygonIcon from '../../assets/images/point-polygon-icon.png'
import pointbarPolygonCompletedIcon from '../../assets/images/point-polygon-completed-icon.png'

interface PointBarProps {
  absolutePositioning?: boolean
  totalExperiencePoints?: number
  isAddingPoint?: boolean
  plusPoint?: number
  isCompleted?: boolean
};

export const POINT_BAR_HORIZONTAL_INSET_CLASS = 'px-[95px]'

const safeFillPercent = 100
const safeGlowPercent = 96.5

/** Health bar. Displays the user experience points.
 */
const PointBar = forwardRef<HTMLDivElement, PointBarProps>(({
  absolutePositioning = true,
  totalExperiencePoints = 0,
  isAddingPoint = false,
  plusPoint = 0,
  isCompleted = false
}, ref) => {
  const newPoints = totalExperiencePoints
  const hasJustCompleted = newPoints >= 100

  const glowWidth = Math.min(newPoints, safeGlowPercent)
  const fillWidth = Math.min(newPoints, safeFillPercent)

  return (
    <div className={POINT_BAR_HORIZONTAL_INSET_CLASS}>
      <div className='relative'>
        <div
          ref={ref}
          className={absolutePositioning ? 'absolute w-full top-[-40px] z-10' : 'w-full top-[21px] z-10'}
          data-testid='point-bar-container'
        >
          <div className='pl-[46px]'>
            <div className='relative py-7'>
              {!isCompleted && (
                <div className='absolute glow-effect bg-dopage-lightGreen h-[40px] left-[12px]' style={{ width: `${glowWidth}%` }}>
                  <div className='relative' />
                </div>
              )}
              <div className={clsx('duration-[1000ms] ease-out relative w-full h-[40px] pointBar', { 'bg-dopage-lightGreen': !isCompleted && !hasJustCompleted, 'bg-dopage-primary': isCompleted || hasJustCompleted })}>
                <div className='absolute inside left-[0px] right-[6px] top-[5px] bottom-[5px] bg-black' />
                <div className='absolute inside left-[47px] right-[6px] top-[5px] bottom-[5px] bg-black'>
                  <div className='glow-effect h-full' style={{ width: `${fillWidth}%` }}>
                    <div className='relative h-full fill duration-500 ease-out flex items-center'>
                      {!isCompleted
                        ? (
                          <div
                            className='absolute font-bold text-[26px] right-[12px] top-[50%] -translate-y-[50%] tracking-tight'
                            role='complementary'
                            aria-label='Training progress'
                            aria-live='polite'
                          >
                            <span className='sr-only'>Health bar progress: </span>
                            <span>{fillWidth}</span>
                            <span className='sr-only'> XP</span>
                          </div>
                          )
                        : null}
                    </div>
                  </div>
                </div>
                <div
                  className='absolute left-[57px] top-[50%] -translate-y-[50%] text-dopage-lightGreen font-bold tracking-tight text-[26px] duration-[1000ms] ease-linear'
                  style={{ opacity: isAddingPoint ? 1 : 0 }}
                  aria-hidden='true'
                >
                  +{plusPoint} XP
                </div>
              </div>
              <div className='absolute -left-[46px] top-[50%] -translate-y-[50%] duration-[1000ms] ease-out'>
                <img src={isCompleted || hasJustCompleted ? pointbarPolygonCompletedIcon : pointbarPolygonIcon} width={100} height={100} alt='point icon' aria-hidden='true' />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
})

export default PointBar
